'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { API_BASE, COMPANY_ID, browserCanReachKit } from '@/lib/kit-client';
import { visitorId } from '@/lib/visitor-id';
import { markAssigned, trackConversion } from '@/lib/experiments';

/**
 * Applies the owner's running A/B tests to this page.
 *
 * The owner builds an experiment in the KIT dashboard; this component asks KIT
 * which variant the visitor is in (`POST /experiments/assign`, sticky per
 * visitor) and swaps the matching text on the page.
 *
 *   - An `external_field` experiment targets one registered CMS field, e.g.
 *     hero-main.headline. We swap the text of any element tagged
 *     `data-kit-field="hero-main.headline"`.
 *   - A `hero` experiment carries `headline`, `ctaText` and `ctaLink`, applied
 *     to the elements tagged `hero-main.headline`, `hero-main.ctaLabel` and
 *     `data-kit-hero-cta`.
 *
 * Only elements we have tagged can change, and only their text (and the hero
 * button's link, restricted to a same-site path or https URL). An experiment
 * pointing at a field we have not tagged is skipped, not guessed at.
 *
 * With no running experiment the call returns an empty list and nothing on the
 * page changes. The component renders nothing and is mounted only while the
 * `abTesting` module is switched on.
 *
 * Known trade-off: pages are cached, so the variant cannot be chosen at render
 * time. The default text shows first and is swapped when the answer arrives,
 * normally within a fraction of a second. Edits go onto the existing text node
 * so React's own tree stays valid.
 */

type Assigned = {
  id: string;
  goal: string;
  target: { type: 'hero' | 'external_field'; componentId?: string; fieldId?: string };
  variant: {
    key: string;
    value?: unknown;
    changes?: { headline?: string; ctaText?: string; ctaLink?: string };
  };
};

const API_KEY = process.env.NEXT_PUBLIC_KIT_API_KEY || '';

/** Same-site path or https URL only: a variant can never inject javascript:. */
function safeHref(href: string): string | null {
  if (/^\/(?!\/)/.test(href)) return href;
  try {
    const u = new URL(href);
    return u.protocol === 'https:' ? u.toString() : null;
  } catch {
    return null;
  }
}

function setText(el: Element, value: string) {
  const only = el.childNodes.length === 1 ? el.firstChild : null;
  if (only && only.nodeType === Node.TEXT_NODE) only.nodeValue = value;
  else el.textContent = value;
}

function applyField(key: string, value: string): boolean {
  const els = document.querySelectorAll(`[data-kit-field="${CSS.escape(key)}"]`);
  els.forEach((el) => setText(el, value));
  return els.length > 0;
}

function apply(exp: Assigned): boolean {
  if (exp.target.type === 'external_field') {
    const { componentId, fieldId } = exp.target;
    const value = exp.variant.value;
    if (!componentId || !fieldId || typeof value !== 'string' || !value) return false;
    return applyField(`${componentId}.${fieldId}`, value);
  }

  const ch = exp.variant.changes || {};
  let applied = false;
  if (ch.headline) applied = applyField('hero-main.headline', ch.headline) || applied;
  if (ch.ctaText) applied = applyField('hero-main.ctaLabel', ch.ctaText) || applied;
  if (ch.ctaLink) {
    const href = safeHref(ch.ctaLink);
    const a = document.querySelector<HTMLAnchorElement>('[data-kit-hero-cta]');
    if (href && a) {
      a.setAttribute('href', href);
      applied = true;
    }
  }
  return applied;
}

export function AbTesting() {
  const pathname = usePathname();

  // Assign on every navigation: tagged elements differ per page.
  useEffect(() => {
    if (!API_KEY || !browserCanReachKit()) return;
    const vid = visitorId();
    if (!vid) return; // no consent for analytics: show the page as it is, assign nothing
    const ctrl = new AbortController();

    fetch(`${API_BASE}/api/${COMPANY_ID}/experiments/assign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-company-key': API_KEY },
      body: JSON.stringify({ visitorId: vid }),
      signal: ctrl.signal,
    })
      .then((r) => (r.ok ? r.json() : { experiments: [] }))
      .then((d: { experiments?: Assigned[] }) => {
        for (const exp of d.experiments ?? []) {
          // Remember the goal even if this page has nothing to swap: the
          // visitor IS in the experiment, and may convert on another page.
          markAssigned(exp.goal);
          apply(exp);
        }
      })
      .catch(() => {});

    return () => ctrl.abort();
  }, [pathname]);

  // `cta_click` wins: any element tagged data-kit-cta that is clicked.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if ((e.target as Element | null)?.closest?.('[data-kit-cta]')) trackConversion('cta_click');
    };
    document.addEventListener('click', onClick, { passive: true });
    return () => document.removeEventListener('click', onClick);
  }, []);

  return null;
}
